
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  HiOutlineArrowLeft,
  HiOutlineArrowRight,
  HiOutlinePause,
  HiOutlinePlay,
} from "react-icons/hi2";

import { getAllReviews } from "../redux/actions/reviews";
import { Loader, ReviewsCard } from "../components/utils";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, A11y } from "swiper/modules";

import "swiper/css";

/* ============================================
   REVIEWS SECTION
============================================ */

function Reviews() {
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();

  const swiperRef = useRef(null);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const { isLoading, AllReviews } = useSelector(
    (state) => state.Reviews || {}
  );

  const language = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en")
    ? "en"
    : "ar";

  const isEnglish = language === "en";

  /* ======================================
     FETCH REVIEWS
  ====================================== */

  useEffect(() => {
    dispatch(getAllReviews(1, 10));
  }, [dispatch]);

  const reviews = Array.isArray(AllReviews)
    ? AllReviews
    : [];

  const totalReviews = reviews.length;

  /* ======================================
     LOCAL LABELS
  ====================================== */

  const labels = {
    eyebrow: t("Our success partners"),
    title: t("What They Say?"),

    description: isEnglish
      ? "Perspectives and experiences shared through our reviews section."
      : "آراء وتجارب نعرضها في قسم المراجعات.",

    previous: isEnglish
      ? "Previous review"
      : "المراجعة السابقة",

    next: isEnglish
      ? "Next review"
      : "المراجعة التالية",

    pause: isEnglish
      ? "Pause autoplay"
      : "إيقاف العرض التلقائي",

    play: isEnglish
      ? "Resume autoplay"
      : "استئناف العرض التلقائي",

    slide: isEnglish
      ? "Go to review"
      : "الانتقال إلى المراجعة",

    empty: isEnglish
      ? "No reviews are available yet."
      : "لا توجد مراجعات متاحة حاليًا.",
  };

  /* ======================================
     SLIDER CONTROLS
  ====================================== */

  const goToSlide = (index) => {
    const swiper = swiperRef.current;

    if (!swiper || swiper.destroyed) return;

    if (swiper.params.loop) {
      swiper.slideToLoop(index);
    } else {
      swiper.slideTo(index);
    }
  };

  const nextSlide = () => {
    swiperRef.current?.slideNext();
  };

  const previousSlide = () => {
    swiperRef.current?.slidePrev();
  };

  const toggleAutoplay = () => {
    const swiper = swiperRef.current;

    if (!swiper?.autoplay) return;

    if (isPaused) {
      swiper.autoplay.start();
      setIsPaused(false);
    } else {
      swiper.autoplay.stop();
      setIsPaused(true);
    }
  };

  const showControls = totalReviews > 1;

  /* ======================================
     SECTION
  ====================================== */

  return (
    <section
      id="client-reviews"
      aria-labelledby="reviews-heading"
      className="
        relative isolate
        w-full overflow-hidden

        border-t border-skin-border
        bg-white

        min-h-[600px]
        py-12

        lg:h-[680px]
        lg:py-14

        dark:border-white/10
        dark:bg-[#0B1726]

        transition-colors duration-300
      "
    >
      {/* ======================================
          BACKGROUND DECORATION
      ====================================== */}

      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute -end-32 -top-40
          h-[400px] w-[400px]
          rounded-full
          bg-[#C19B60]/[0.035]
          blur-3xl
        "
      />

      <div className="container relative flex h-full flex-col justify-center">

        {/* ======================================
            SECTION HEADING
        ====================================== */}

        <div
          className="
            mb-8 flex flex-col
            items-center text-center
            lg:mb-10
          "
        >
          {/* Eyebrow */}
          <div className="mb-4 flex items-center gap-3">

            <span
              aria-hidden="true"
              className="h-px w-8 bg-skin-accent"
            />

            <span className="eyebrow text-skin-accent">
              {labels.eyebrow}
            </span>

            <span
              aria-hidden="true"
              className="h-px w-8 bg-skin-accent"
            />

          </div>

          {/* Main title */}
          <h2
            id="reviews-heading"
            className="
              section-title
              text-skin-heading
            "
          >
            {labels.title}
          </h2>

          {/* Description */}
          <p
            className="
              body-3 mt-3
              max-w-[600px]
              text-skin-muted
            "
          >
            {labels.description}
          </p>

        </div>

        {/* ======================================
            REVIEWS SLIDER
        ====================================== */}

        {isLoading ? (
          <div className="flex h-[320px] items-center justify-center">
            <Loader />
          </div>
        ) : totalReviews > 0 ? (
          <>
            <div
              className="
                relative w-full
                overflow-hidden
                px-2
                py-3
              "
            >
              <Swiper
                key={language}
                modules={[Autoplay, A11y]}
                effect="slide"
                centeredSlides={totalReviews > 1}
                slidesPerView={1}
                spaceBetween={20}
                speed={700}
                grabCursor={totalReviews > 1}
                allowTouchMove={totalReviews > 1}
                loop={totalReviews > 3}
                watchSlidesProgress={true}
                breakpoints={{
                  640: {
                    slidesPerView: 1.3,
                    spaceBetween: 20,
                  },
                  768: {
                    slidesPerView: 2,
                    spaceBetween: 22,
                  },
                  1100: {
                    slidesPerView: 3,
                    spaceBetween: 24,
                  },
                }}
                autoplay={
                  totalReviews > 1 && !isPaused
                    ? {
                        delay: 5000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true,
                      }
                    : false
                }
                onSwiper={(swiper) => {
                  swiperRef.current = swiper;
                  setActiveIndex(swiper.realIndex);
                }}
                onSlideChange={(swiper) => {
                  setActiveIndex(swiper.realIndex);
                }}
                a11y={{
                  enabled: true,
                }}
                className="!overflow-visible !py-5"
              >
                {reviews.map((review, index) => (
                  <SwiperSlide
                    key={review._id || index}
                    className="!h-auto"
                  >
                    {({ isActive }) => (
                      <ReviewsCard
                        reviews={review}
                        isActive={isActive}
                      />
                    )}
                  </SwiperSlide>
                ))}

              </Swiper>
            </div>

            {/* ==================================
                SLIDER CONTROLS
            ================================== */}

            {showControls && (
              <div
                className="
                  mt-5 flex flex-wrap
                  items-center justify-between
                  gap-5
                  border-t border-skin-border
                  pt-5

                  lg:mt-6
                "
              >
                {/* Navigation buttons */}
                <div className="flex items-center gap-2">

                  <button
                    type="button"
                    onClick={previousSlide}
                    aria-label={labels.previous}
                    className="
                      flex h-11 w-11
                      items-center justify-center

                      rounded-xl
                      border border-skin-border
                      bg-skin-surface

                      text-skin-heading

                      transition-all duration-300

                      hover:border-skin-accent/40
                      hover:bg-skin-soft

                      focus-visible:outline-none
                      focus-visible:ring-2
                      focus-visible:ring-skin-accent
                    "
                  >
                    <HiOutlineArrowLeft
                      size={20}
                      aria-hidden="true"
                      className="directional-arrow"
                    />
                  </button>

                  <button
                    type="button"
                    onClick={nextSlide}
                    aria-label={labels.next}
                    className="
                      flex h-11 w-11
                      items-center justify-center

                      rounded-xl
                      border border-skin-border
                      bg-skin-surface

                      text-skin-heading

                      transition-all duration-300

                      hover:border-skin-accent/40
                      hover:bg-skin-soft

                      focus-visible:outline-none
                      focus-visible:ring-2
                      focus-visible:ring-skin-accent
                    "
                  >
                    <HiOutlineArrowRight
                      size={20}
                      aria-hidden="true"
                      className="directional-arrow"
                    />
                  </button>

                  {/* Pause / Play */}
                  <button
                    type="button"
                    onClick={toggleAutoplay}
                    aria-label={
                      isPaused ? labels.play : labels.pause
                    }
                    aria-pressed={isPaused}
                    className="
                      ms-2 flex h-11 w-11
                      items-center justify-center

                      rounded-xl
                      border border-skin-border
                      bg-skin-surface

                      text-skin-muted

                      transition-all duration-300

                      hover:bg-skin-soft
                      hover:text-skin-heading

                      focus-visible:outline-none
                      focus-visible:ring-2
                      focus-visible:ring-skin-accent
                    "
                  >
                    {isPaused ? (
                      <HiOutlinePlay
                        size={19}
                        aria-hidden="true"
                      />
                    ) : (
                      <HiOutlinePause
                        size={19}
                        aria-hidden="true"
                      />
                    )}
                  </button>

                </div>

                {/* Pagination */}
                <div className="flex items-center gap-2">

                  {reviews.map((review, index) => {
                    const isActive = activeIndex === index;

                    return (
                      <button
                        key={review._id || index}
                        type="button"
                        onClick={() => goToSlide(index)}
                        aria-label={`${labels.slide} ${index + 1}`}
                        aria-current={
                          isActive ? "true" : undefined
                        }
                        className="
                          group flex h-9
                          items-center justify-center

                          focus-visible:outline-none
                          focus-visible:ring-2
                          focus-visible:ring-skin-accent
                        "
                      >
                        <span
                          className={`
                            block h-[3px]
                            rounded-full

                            transition-all duration-300

                            ${
                              isActive
                                ? "w-9 bg-skin-accent"
                                : "w-4 bg-skin-border group-hover:bg-skin-accent/50"
                            }
                          `}
                        />
                      </button>
                    );
                  })}

                </div>

                {/* Review counter */}
                <div
                  className="
                    hidden items-center gap-2
                    font-cairo text-sm
                    tabular-nums
                    sm:flex
                  "
                >
                  <span className="font-semibold text-skin-heading">
                    {String(activeIndex + 1).padStart(2, "0")}
                  </span>

                  <span className="text-skin-muted">/</span>

                  <span className="text-skin-muted">
                    {String(totalReviews).padStart(2, "0")}
                  </span>
                </div>

              </div>
            )}

          </>
        ) : (
          <div
            className="
              flex h-[320px]
              items-center justify-center
              text-center
            "
          >
            <p className="body-2 text-skin-muted">
              {labels.empty}
            </p>
          </div>
        )}

      </div>
    </section>
  );
}

export default Reviews;
